// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// ── An alternate is a decision, not a line ───────────────────────────────────
//
// Alternate-ness used to live on the line item: altType said "Alt" or "Void",
// altStatus said "TBD" or "Accepted", and every total walked the lines
// independently. That model cannot express the thing an owner is actually
// being asked, which is "do we add the bathroom?" -- a question whose answer
// touches plumbing, drywall, fixtures and MEPs across four divisions.
//
// Two things went wrong because of it.
//
// Partial acceptance was possible. Six lines make up the bathroom, you accept
// four, and the estimate now contains most of a bathroom. Every total still
// adds up, which is what makes it dangerous: there is no figure on screen
// that looks wrong.
//
// And a scope could land in both buckets at once. The old classifier was
// `amt >= 0 ? add : deduct`, decided per line. A bathroom add that credits
// the closet it replaces has a negative line, so that line was filed under
// Deduct Alternates -- one decision, two buckets, and neither headline total
// describing anything real.
//
// So the package is the unit. Lines point at it the way they already point at
// a proposal, the status lives on it, and add-versus-deduct is decided once,
// on the net.

import { walkItems, getItemTotal } from "./calculations";

export type AltStatus = "TBD" | "Accepted" | "Rejected";

export interface AltPackage {
  id: string;
  /** What the owner is being asked, e.g. "Add guest bathroom". */
  name: string;
  /**
   * The last answer written through this package. Kept so a saved project
   * reads sensibly on its own, but it is NOT what the UI shows -- the status
   * in a rollup is derived from the lines. See statusOfLines.
   */
  status: AltStatus;
  /** Optional prose for the owner-facing sheet. */
  scopeNote?: string;
  /** Optional date the price is held to, or the decision is needed by. */
  decideBy?: string;
}

/** A package with its lines and their arithmetic resolved. */
export interface AltRollup {
  /** `status` here is the derived one, which may differ from the record. */
  pkg: AltPackage;
  lines: Array<{ id: string; desc: string; div: string; amt: number }>;
  /** Adds and credits inside one decision, summed. The number the owner sees. */
  net: number;
  /** Positive parts only -- what the scope costs before its own credits. */
  gross: number;
  /** Negative parts only, as a positive number. */
  credits: number;
  /** Which bucket the decision belongs in, decided once on the net. */
  kind: "add" | "deduct";
  /** The lines disagree about whether this scope is in. */
  mixed: boolean;
}

export const ALT_STATUSES: AltStatus[] = ["TBD", "Accepted", "Rejected"];

/**
 * A line that has never been grouped stands in for itself, under a synthetic
 * id. Every alternate in an existing project is one of these, and they have to
 * accept a decision exactly like a real package does -- otherwise the tab is
 * read-only until somebody migrates their data, which nobody will.
 */
const LINE_PKG = "line:";
export const isLinePackage = (id: string) => id.startsWith(LINE_PKG);
export const linePackageId = (itemId: string) => LINE_PKG + itemId;
const lineIdOf = (pkgId: string) => pkgId.slice(LINE_PKG.length);

/** Accepted alternates are the only ones inside the base bid. */
export const isAcceptedAlt = (s: string | undefined) => s === "Accepted";

/** What one line says about itself, in package terms. */
export const statusOfLine = (li: any): AltStatus =>
  li?.altType === "Void" ? "Rejected" : li?.altStatus === "Accepted" ? "Accepted" : "TBD";

/**
 * The answer a set of lines adds up to.
 *
 * The package record carries a status too, but it is not the one that counts.
 * Every total in the product -- the base bid, the division rolls, cost per
 * square foot -- gates on the line's own `altStatus`, and the Estimate tab can
 * still set that per line. If the package were the authority, accepting one
 * here would move a number on this tab and nothing else, which is the worst
 * kind of wrong: an authoritative-looking figure that reconciles with nothing.
 *
 * So the lines are the source of truth and the package is how you write them
 * all at once. When they disagree -- somebody edited one line in the Estimate
 * tab -- that shows as mixed rather than being silently resolved.
 */
export function statusOfLines(lines: any[]): { status: AltStatus; mixed: boolean } {
  if (!lines.length) return { status: "TBD", mixed: false };
  const each = lines.map(statusOfLine);
  const first = each[0];
  if (each.every(s => s === first)) return { status: first, mixed: false };
  return { status: "TBD", mixed: true };
}

/**
 * Gather every alternate line into its package.
 *
 * A line tagged as an alternate with no package of its own is not dropped --
 * it becomes a package of one, named after itself. That is what every
 * alternate in an existing project looks like, and it has to keep working
 * without anybody migrating anything.
 */
export function rollUpAlternates(
  divisions: any[] | null | undefined,
  itemState: Record<string, any> | null | undefined,
  packages: AltPackage[] | null | undefined,
): AltRollup[] {
  const st = itemState || {};
  const byId = new Map<string, AltPackage>((packages || []).map(p => [p.id, p]));
  const lines = new Map<string, AltRollup["lines"]>();
  const states = new Map<string, any[]>();
  const implied = new Map<string, AltPackage>();

  (divisions || []).forEach((div: any) =>
    (div?.subcategories || []).forEach((sub: any) =>
      [...walkItems(sub?.rows || [])].forEach((item: any) => {
        const li = st[item.id] || {};
        if (!li.included) return;
        if (li.altType !== "Alt" && li.altType !== "Void") return;

        const pkgId: string = li.altPackageId || linePackageId(item.id);
        if (!byId.has(pkgId) && !implied.has(pkgId)) {
          // A package of one, standing in for a line that has never been
          // grouped.
          implied.set(pkgId, {
            id: pkgId,
            name: li.description || item.description || "Untitled alternate",
            status: statusOfLine(li),
          });
        }
        const amt = getItemTotal(li, st);
        const row = { id: item.id, desc: li.description || item.description || "", div: div.code || div.name || "", amt };
        const arr = lines.get(pkgId);
        if (arr) arr.push(row); else lines.set(pkgId, [row]);
        const sts = states.get(pkgId);
        if (sts) sts.push(li); else states.set(pkgId, [li]);
      }),
    ),
  );

  const out: AltRollup[] = [];
  for (const [pkgId, rows] of lines) {
    const record = byId.get(pkgId) || implied.get(pkgId)!;
    const { status, mixed } = statusOfLines(states.get(pkgId) || []);
    // The record's name, the lines' answer.
    const pkg: AltPackage = { ...record, status };
    const net = rows.reduce((a, r) => a + r.amt, 0);
    const gross = rows.filter(r => r.amt > 0).reduce((a, r) => a + r.amt, 0);
    const credits = -rows.filter(r => r.amt < 0).reduce((a, r) => a + r.amt, 0);
    out.push({ pkg, lines: rows, net, gross, credits, kind: net < 0 ? "deduct" : "add", mixed });
  }
  // Biggest decisions first: that is the order an owner meeting takes them in.
  out.sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
  return out;
}

export interface AltSummary {
  adds: AltRollup[];
  deducts: AltRollup[];
  rejected: AltRollup[];
  /** Net of accepted adds and deducts -- already inside the base bid. */
  acceptedNet: number;
  /** Net of everything still undecided. */
  pendingNet: number;
  /** How far the bid could move if every pending add were taken. */
  maxUp: number;
  /** How far it could move if every pending deduct were taken. */
  maxDown: number;
  /** Decisions outstanding. Packages, not lines. */
  pendingCount: number;
}

export function summarizeAlternates(rollups: AltRollup[]): AltSummary {
  const live = rollups.filter(r => r.pkg.status !== "Rejected");
  const rejected = rollups.filter(r => r.pkg.status === "Rejected");
  const pending = live.filter(r => r.pkg.status !== "Accepted");
  return {
    adds: live.filter(r => r.kind === "add"),
    deducts: live.filter(r => r.kind === "deduct"),
    rejected,
    acceptedNet: live.filter(r => isAcceptedAlt(r.pkg.status)).reduce((a, r) => a + r.net, 0),
    pendingNet: pending.reduce((a, r) => a + r.net, 0),
    maxUp: pending.filter(r => r.net > 0).reduce((a, r) => a + r.net, 0),
    maxDown: -pending.filter(r => r.net < 0).reduce((a, r) => a + r.net, 0),
    pendingCount: pending.length,
  };
}

/**
 * The patch that puts a set of lines into a package.
 *
 * Returned rather than applied so the caller owns the undo snapshot, and so
 * this stays testable without a store.
 */
export function assignToPackage(
  itemState: Record<string, any>,
  lineIds: string[],
  pkgId: string | null,
): Record<string, any> {
  const next = { ...itemState };
  for (const id of lineIds) {
    if (!next[id]) continue;
    next[id] = { ...next[id], altPackageId: pkgId ?? undefined, altType: next[id].altType || "Alt" };
  }
  return next;
}

/**
 * Fold several decisions into one.
 *
 * This is the move the model exists for: six lines that were six questions
 * become "Add guest bathroom", priced at their net, answered once.
 */
export function combineIntoPackage(
  packages: AltPackage[],
  itemState: Record<string, any>,
  lineIds: string[],
  name: string,
  status: AltStatus = "TBD",
  id: string = `altpkg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
): { packages: AltPackage[]; itemState: Record<string, any>; id: string } {
  const pkg: AltPackage = { id, name: name || "Untitled alternate", status };
  const { itemState: next } = setPackageStatus(
    [...packages, pkg], assignToPackage(itemState, lineIds, id), id, status,
  );
  return { packages: [...packages, pkg], itemState: next, id };
}

/**
 * Take a package apart, back into the lines it was made of.
 *
 * Each line already carries the answer -- that is where it lives -- so this
 * only drops the pointer and the record.
 */
export function splitPackage(
  packages: AltPackage[],
  itemState: Record<string, any>,
  pkgId: string,
): { packages: AltPackage[]; itemState: Record<string, any> } {
  if (isLinePackage(pkgId)) return { packages, itemState };
  const next = { ...itemState };
  for (const [id, li] of Object.entries(itemState)) {
    if (li?.altPackageId !== pkgId) continue;
    next[id] = { ...li, altPackageId: undefined };
  }
  return { packages: packages.filter(p => p.id !== pkgId), itemState: next };
}

/** The pair of fields a line carries for a given answer. */
const lineFieldsFor = (status: AltStatus) =>
  status === "Rejected" ? { altType: "Void", altStatus: "Void" }
  : status === "Accepted" ? { altType: "Alt", altStatus: "Accepted" }
  : { altType: "Alt", altStatus: "TBD" };

/**
 * Answer a package, by writing the same answer onto every line in it.
 *
 * This is the hole the package model exists to close. Accepting four of six
 * lines used to leave the estimate holding most of a bathroom, with every
 * total still adding up -- nothing on screen looked wrong. One call, every
 * line, no partial scope.
 *
 * It writes the lines rather than holding the answer on the package because
 * the lines are what every other total reads. See statusOfLines.
 */
export function setPackageStatus(
  packages: AltPackage[],
  itemState: Record<string, any>,
  pkgId: string,
  status: AltStatus,
): { packages: AltPackage[]; itemState: Record<string, any> } {
  const nextItems = { ...itemState };
  const fields = lineFieldsFor(status);

  // An ungrouped line has no package record; it is only itself.
  if (isLinePackage(pkgId)) {
    const id = lineIdOf(pkgId);
    if (nextItems[id]) nextItems[id] = { ...nextItems[id], ...fields };
    return { packages, itemState: nextItems };
  }

  for (const [id, li] of Object.entries(itemState)) {
    if (li?.altPackageId !== pkgId) continue;
    nextItems[id] = { ...li, ...fields };
  }
  return { packages: packages.map(p => (p.id === pkgId ? { ...p, status } : p)), itemState: nextItems };
}
